import { useMemo, useState } from 'react';
import {
  Accordion,
  Button,
  Checkbox,
  Confirm,
  Form,
  Grid,
  Label,
  List,
  Message,
  Segment,
} from 'semantic-ui-react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import type {
  DimensaoRestritiva,
  MpfPerfil,
  MpfPrincipal,
} from 'volto-ministerio-publico-federal/helpers/Permissoes';
import { agruparPor, nomeDoLayout } from './agrupamentos';
import type { BlocoOpcao } from './catalogoBlocos';
import PrincipaisPicker from './PrincipaisPicker';

type Opcao = { id: string; title: string; description?: string };

export type Catalogo = {
  controlpanels: {
    id: string;
    title: string;
    description?: string;
    category: string;
    category_title: string;
    delegavel: boolean;
    motivo: string | null;
  }[];
  extras: (Opcao & { group: string })[];
  toolbar: Opcao[];
  types: Opcao[];
  layouts: { id: string; types: string[] }[];
  fields?: Opcao[];
  blocks_obrigatorios: string[];
};

const messages = defineMessages({
  secaoGeral: {
    id: 'Access profile section general',
    defaultMessage: 'Identificação',
  },
  secaoPaineis: {
    id: 'Access profile section control panels',
    defaultMessage: 'Painéis de controle',
  },
  secaoExtras: {
    id: 'Access profile section extras',
    defaultMessage: 'Permissões adicionais',
  },
  secaoToolbar: {
    id: 'Access profile section toolbar',
    defaultMessage: 'Toolbar',
  },
  secaoTypes: { id: 'Access profile section types', defaultMessage: 'Tipos' },
  secaoLayouts: {
    id: 'Access profile section layouts',
    defaultMessage: 'Layouts',
  },
  secaoBlocks: {
    id: 'Access profile section blocks',
    defaultMessage: 'Blocos',
  },
  secaoFields: {
    id: 'Access profile section fields',
    defaultMessage: 'Campos do formulário',
  },
  ajudaFields: {
    id: 'Access profile help fields',
    defaultMessage:
      'Afeta abas e campos do formulário de edição. Um campo escondido some do formulário e a API recusa a alteração. SEO e Menu já vêm fechados para editores comuns: para liberar, marque “Editar SEO” ou “Configurar exibição no menu” em “Permissões adicionais”.',
  },
  secaoPrincipals: {
    id: 'Access profile section principals',
    defaultMessage: 'Grupos e usuários',
  },
  secaoPermissoes: {
    id: 'Access profile section permissions',
    defaultMessage: 'Permissões e restrições',
  },
  ajudaPermissoes: {
    id: 'Access profile help permissions',
    defaultMessage:
      '“Painéis de controle” e “Permissões adicionais” LIBERAM acessos. “Toolbar”, “Tipos”, “Layouts” e “Blocos” só RESTRINGEM o que a pessoa já poderia fazer pelo papel dela (Editor, Colaborador...).',
  },
  id: { id: 'Access profile id', defaultMessage: 'Identificador' },
  idHelp: {
    id: 'Access profile id help',
    defaultMessage:
      'Letras minúsculas, números e _ (2 a 40). Não pode ser alterado depois.',
  },
  title: { id: 'Access profile title', defaultMessage: 'Nome do perfil' },
  description: {
    id: 'Access profile description',
    defaultMessage: 'Descrição',
  },
  ajudaGeral: {
    id: 'Access profile help general',
    defaultMessage:
      'Um perfil é um papel do Plone, sempre abaixo do administrador (Manager). O que ele libera ou restringe vale no site inteiro, na interface e na API.',
  },
  ajudaPaineis: {
    id: 'Access profile help control panels',
    defaultMessage:
      'Libera o painel no Site Setup (/controlpanel) e na API @controlpanels. Quem recebe algum painel passa a ver o Site Setup só com os painéis liberados. Painéis que permitem injetar código no site, trocar o servidor de e-mail ou mudar a segurança não podem ser delegados — aparecem desabilitados, com o motivo ao passar o mouse.',
  },
  ajudaExtras: {
    id: 'Access profile help extras',
    defaultMessage:
      'Somam-se às permissões que a pessoa já tem pelo papel. Leia a explicação de cada item: ela diz em que tela a permissão aparece.',
  },
  ajudaToolbar: {
    id: 'Access profile help toolbar',
    defaultMessage:
      'Afeta a barra de ferramentas lateral de quem está logado. Sem restrição, aparece o que o papel da pessoa já permite. Com restrição, só os itens marcados aparecem, e as ações escondidas também são recusadas pela API (exceto “Conteúdos”).',
  },
  ajudaTypes: {
    id: 'Access profile help types',
    defaultMessage:
      'Afeta o botão “Adicionar” e a criação de conteúdo pela API. Só restringe: não dá permissão de adicionar a quem não tem (para isso, veja “Permissões adicionais”).',
  },
  ajudaLayouts: {
    id: 'Access profile help layouts',
    defaultMessage:
      'Afeta as opções de Mais → Visualização e a troca de layout pela API. O layout que um conteúdo já usa continua valendo; só a troca para um layout fora da lista é recusada.',
  },
  ajudaBlocks: {
    id: 'Access profile help blocks',
    defaultMessage:
      'Afeta o seletor de blocos (botão “+” e o menu “/” no texto) ao editar páginas e slots. Blocos que já estão numa página continuam lá; a API recusa apenas blocos novos fora da lista.',
  },
  ajudaPrincipals: {
    id: 'Access profile help principals',
    defaultMessage:
      'Todo membro de um grupo listado recebe o perfil enquanto estiver no grupo. Tirar a pessoa do grupo, remover o grupo daqui ou excluir o grupo retira o acesso na hora — a interface dela atualiza no próximo carregamento de página. As mudanças desta seção só valem ao salvar.',
  },
  restrict: {
    id: 'Access profile restrict dimension',
    defaultMessage: 'Restringir: {dimensao}',
  },
  semRestricao: {
    id: 'Access profile no restriction',
    defaultMessage:
      'Sem restrição: vale o que as permissões do usuário já permitem.',
  },
  semRestricaoCurto: {
    id: 'Access profile no restriction short',
    defaultMessage: 'livre',
  },
  marcarTodos: {
    id: 'Access profile select all',
    defaultMessage: 'Marcar todos',
  },
  desmarcarTodos: {
    id: 'Access profile select none',
    defaultMessage: 'Desmarcar todos',
  },
  outros: { id: 'Access profile other group', defaultMessage: 'Outros' },
  oferecidoPor: {
    id: 'Access profile layout offered by',
    defaultMessage: 'Tipos: {tipos}',
  },
  removePrincipal: {
    id: 'Access profile remove principal',
    defaultMessage: 'Remover',
  },
  grupo: { id: 'Access profile principal group', defaultMessage: 'grupo' },
  usuario: { id: 'Access profile principal user', defaultMessage: 'usuário' },
  removido: {
    id: 'Access profile principal removed',
    defaultMessage: 'não existe mais',
  },
  confirmarRemoverGrupo: {
    id: 'Access profile confirm remove group',
    defaultMessage:
      'Remover o grupo “{title}” deste perfil? Ao salvar, TODOS os membros do grupo perdem na hora o que o perfil libera — a menos que recebam o perfil diretamente ou por outro grupo.',
  },
  confirmarRemoverUsuario: {
    id: 'Access profile confirm remove user',
    defaultMessage:
      'Remover “{title}” deste perfil? Ao salvar, a pessoa perde na hora o que o perfil libera — a menos que o receba também por um grupo.',
  },
  confirmarRemoverOrfao: {
    id: 'Access profile confirm remove orphan',
    defaultMessage:
      '“{title}” não existe mais (usuário ou grupo excluído). Remover a atribuição que sobrou?',
  },
  cancel: { id: 'Cancel', defaultMessage: 'Cancelar' },
});

const alternar = (lista: string[], item: string) =>
  lista.includes(item) ? lista.filter((x) => x !== item) : [...lista, item];

const Ajuda = ({ children }: { children: string }) => (
  <Message info size="small" className="mpf-perfil-ajuda">
    {children}
  </Message>
);

const ItemComDescricao = ({
  opcao,
  checked,
  disabled,
  title,
  onChange,
}: {
  opcao: Opcao;
  checked: boolean;
  disabled?: boolean;
  title?: string;
  onChange: () => void;
}) => (
  <List.Item title={title}>
    <Checkbox
      label={opcao.title}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
    {opcao.description && (
      <List.Description className="mpf-perfil-descricao">
        {opcao.description}
      </List.Description>
    )}
  </List.Item>
);

type Props = {
  perfil: MpfPerfil | null;
  catalogo: Catalogo;
  blocos: BlocoOpcao[];
  gruposDeBlocos?: { id: string; title: string }[];
  nomesDeLayout?: Record<string, string>;
  salvando: boolean;
  erro: string | null;
  onSalvar: (dados: Record<string, unknown>) => void;
  onCancelar: () => void;
};

const PerfilEditor = ({
  perfil,
  catalogo,
  blocos,
  gruposDeBlocos = [],
  nomesDeLayout = {},
  salvando,
  erro,
  onSalvar,
  onCancelar,
}: Props) => {
  const intl = useIntl();
  const traduzir = (texto: string) =>
    intl.formatMessage({ id: texto, defaultMessage: texto });
  const novo = !perfil;
  const [id, setId] = useState(perfil?.id ?? '');
  const [title, setTitle] = useState(perfil?.title ?? '');
  const [description, setDescription] = useState(perfil?.description ?? '');
  const [controlpanels, setControlpanels] = useState<string[]>(
    perfil?.controlpanels ?? [],
  );
  const [extras, setExtras] = useState<string[]>(perfil?.extras ?? []);
  const [restricoes, setRestricoes] = useState<
    Record<DimensaoRestritiva, string[] | null>
  >({
    toolbar: perfil?.toolbar ?? null,
    types: perfil?.types ?? null,
    layouts: perfil?.layouts ?? null,
    blocks: perfil?.blocks ?? null,
    fields: perfil?.fields ?? null,
  });
  const [principais, setPrincipais] = useState<MpfPrincipal[]>(
    perfil?.principals ?? [],
  );
  const [removendo, setRemovendo] = useState<MpfPrincipal | null>(null);

  const opcoesPorDimensao: Record<DimensaoRestritiva, Opcao[]> = useMemo(
    () => ({
      toolbar: catalogo.toolbar,
      types: catalogo.types,
      fields: catalogo.fields ?? [],
      layouts: catalogo.layouts.map((layout) => ({
        id: layout.id,
        title: `${nomeDoLayout(layout.id, nomesDeLayout, (texto) =>
          intl.formatMessage({ id: texto, defaultMessage: texto }),
        )} (${layout.id})`,
        description: intl.formatMessage(messages.oferecidoPor, {
          tipos: layout.types.join(', '),
        }),
      })),
      blocks: blocos.map((bloco) => ({
        id: bloco.id,
        title: intl.formatMessage({
          id: bloco.title,
          defaultMessage: bloco.title,
        }),
      })),
    }),
    [catalogo, blocos, nomesDeLayout, intl],
  );

  const paineisPorGrupo = useMemo(
    () =>
      agruparPor(catalogo.controlpanels, (painel) => painel.category_title).map(
        (grupo) => ({ ...grupo, title: grupo.id }),
      ),
    [catalogo.controlpanels],
  );

  const extrasPorGrupo = useMemo(
    () => agruparPor(catalogo.extras, (extra) => extra.group),
    [catalogo.extras],
  );

  const blocosPorGrupo = useMemo(() => {
    const grupoDe = new Map(blocos.map((bloco) => [bloco.id, bloco.group]));
    return agruparPor(
      opcoesPorDimensao.blocks,
      (bloco) => grupoDe.get(bloco.id),
      gruposDeBlocos.map((grupo) => ({
        id: grupo.id,
        title: intl.formatMessage({
          id: grupo.id,
          defaultMessage: grupo.title,
        }),
      })),
      intl.formatMessage(messages.outros),
    );
  }, [blocos, gruposDeBlocos, opcoesPorDimensao.blocks, intl]);

  const salvar = () => {
    const originais = new Set((perfil?.principals ?? []).map((p) => p.id));
    const atuais = new Set(principais.map((p) => p.id));
    const dados: Record<string, unknown> = {
      title,
      description,
      controlpanels,
      extras,
      ...restricoes,
      principals: {
        add: [...atuais].filter((principal) => !originais.has(principal)),
        remove: [...originais].filter((principal) => !atuais.has(principal)),
      },
    };
    if (novo) dados.id = id;
    onSalvar(dados);
  };

  const contador = (quantidade: number | null) => (
    <Label size="mini" circular={quantidade !== null}>
      {quantidade === null
        ? intl.formatMessage(messages.semRestricaoCurto)
        : quantidade}
    </Label>
  );

  const secaoRestritiva = (
    dimensao: DimensaoRestritiva,
    rotulo: string,
    ajuda: string,
    conteudo: (selecionados: string[]) => React.ReactNode,
  ) => {
    const selecionados = restricoes[dimensao];
    const opcoes = opcoesPorDimensao[dimensao];
    return (
      <>
        <Ajuda>{ajuda}</Ajuda>
        <Checkbox
          toggle
          label={intl.formatMessage(messages.restrict, { dimensao: rotulo })}
          checked={selecionados !== null}
          onChange={(_event, { checked }) =>
            setRestricoes({
              ...restricoes,
              // Ao ligar começa com tudo marcado: o gestor desmarca.
              [dimensao]: checked ? opcoes.map((opcao) => opcao.id) : null,
            })
          }
        />
        {selecionados === null ? (
          <p className="help">{intl.formatMessage(messages.semRestricao)}</p>
        ) : (
          conteudo(selecionados)
        )}
      </>
    );
  };

  const alternarRestricao = (
    dimensao: DimensaoRestritiva,
    selecionados: string[],
    item: string,
  ) =>
    setRestricoes({ ...restricoes, [dimensao]: alternar(selecionados, item) });

  const listaSimples =
    (dimensao: DimensaoRestritiva) => (selecionados: string[]) => (
      <List relaxed className="mpf-perfil-lista">
        {opcoesPorDimensao[dimensao].map((opcao) => (
          <ItemComDescricao
            key={opcao.id}
            opcao={opcao}
            checked={selecionados.includes(opcao.id)}
            onChange={() => alternarRestricao(dimensao, selecionados, opcao.id)}
          />
        ))}
      </List>
    );

  const acordeoesDeBlocos = (selecionados: string[]) => (
    <>
      <Message size="small">
        <FormattedMessage
          id="Access profile mandatory blocks note"
          defaultMessage="Sempre liberados: {blocos}."
          values={{ blocos: catalogo.blocks_obrigatorios.join(', ') }}
        />
      </Message>
      <Accordion.Accordion
        exclusive={false}
        panels={blocosPorGrupo.map((grupo) => {
          const ids = grupo.itens.map((bloco) => bloco.id);
          const marcados = ids.filter((bloco) => selecionados.includes(bloco));
          return {
            key: grupo.id,
            title: {
              content: (
                <>
                  {grupo.title}{' '}
                  <Label size="mini">
                    {marcados.length}/{ids.length}
                  </Label>
                </>
              ),
            },
            content: {
              content: (
                <>
                  <Button
                    type="button"
                    size="mini"
                    basic
                    onClick={() =>
                      setRestricoes({
                        ...restricoes,
                        blocks: [...new Set([...selecionados, ...ids])],
                      })
                    }
                  >
                    {intl.formatMessage(messages.marcarTodos)}
                  </Button>
                  <Button
                    type="button"
                    size="mini"
                    basic
                    onClick={() =>
                      setRestricoes({
                        ...restricoes,
                        blocks: selecionados.filter((b) => !ids.includes(b)),
                      })
                    }
                  >
                    {intl.formatMessage(messages.desmarcarTodos)}
                  </Button>
                  <Grid doubling columns={3}>
                    {grupo.itens.map((bloco) => (
                      <Grid.Column key={bloco.id}>
                        <Checkbox
                          label={bloco.title}
                          checked={selecionados.includes(bloco.id)}
                          onChange={() =>
                            alternarRestricao('blocks', selecionados, bloco.id)
                          }
                        />
                      </Grid.Column>
                    ))}
                  </Grid>
                </>
              ),
            },
          };
        })}
      />
    </>
  );

  const mensagemDeRemocao = (principal: MpfPrincipal | null) => {
    if (!principal) return '';
    const valores = { title: principal.title };
    if ((principal.type as string) === 'removido') {
      return intl.formatMessage(messages.confirmarRemoverOrfao, valores);
    }
    return intl.formatMessage(
      principal.type === 'group'
        ? messages.confirmarRemoverGrupo
        : messages.confirmarRemoverUsuario,
      valores,
    );
  };

  const tipoDoPrincipal = (principal: MpfPrincipal) => {
    if ((principal.type as string) === 'removido') {
      return intl.formatMessage(messages.removido);
    }
    return intl.formatMessage(
      principal.type === 'group' ? messages.grupo : messages.usuario,
    );
  };

  const tituloComContador = (rotulo: string, quantidade: number | null) => ({
    content: (
      <>
        {rotulo} {contador(quantidade)}
      </>
    ),
  });

  const identificacao = (
    <>
      <Ajuda>{intl.formatMessage(messages.ajudaGeral)}</Ajuda>
      <Form.Input
        required
        label={intl.formatMessage(messages.id)}
        value={id}
        disabled={!novo}
        onChange={(_event, { value }) => setId(value)}
      />
      {novo && <p className="help">{intl.formatMessage(messages.idHelp)}</p>}
      <Form.Input
        required
        label={intl.formatMessage(messages.title)}
        value={title}
        onChange={(_event, { value }) => setTitle(value)}
      />
      <Form.TextArea
        label={intl.formatMessage(messages.description)}
        value={description}
        onChange={(_event, { value }) => setDescription(String(value ?? ''))}
      />
    </>
  );

  const paineis = (
    <>
      <Ajuda>{intl.formatMessage(messages.ajudaPaineis)}</Ajuda>
      <Accordion.Accordion
        exclusive={false}
        panels={paineisPorGrupo.map((grupo) => ({
          key: grupo.id,
          title: {
            content: (
              <>
                {grupo.title}{' '}
                <Label size="mini">
                  {
                    grupo.itens.filter((p) => controlpanels.includes(p.id))
                      .length
                  }
                  /{grupo.itens.filter((p) => p.delegavel).length}
                </Label>
              </>
            ),
          },
          content: {
            content: (
              <List relaxed className="mpf-perfil-lista">
                {grupo.itens.map((painel) => (
                  <ItemComDescricao
                    key={painel.id}
                    opcao={painel}
                    checked={controlpanels.includes(painel.id)}
                    disabled={!painel.delegavel}
                    title={painel.motivo ?? undefined}
                    onChange={() =>
                      setControlpanels(alternar(controlpanels, painel.id))
                    }
                  />
                ))}
              </List>
            ),
          },
        }))}
      />
    </>
  );

  const permissoesAdicionais = (
    <>
      <Ajuda>{intl.formatMessage(messages.ajudaExtras)}</Ajuda>
      <Accordion.Accordion
        exclusive={false}
        defaultActiveIndex={extrasPorGrupo.map((_grupo, indice) => indice)}
        panels={extrasPorGrupo.map((grupo) => ({
          key: grupo.id,
          title: grupo.title,
          content: {
            content: (
              <List relaxed className="mpf-perfil-lista">
                {grupo.itens.map((extra) => (
                  <ItemComDescricao
                    key={extra.id}
                    opcao={extra}
                    checked={extras.includes(extra.id)}
                    onChange={() => setExtras(alternar(extras, extra.id))}
                  />
                ))}
              </List>
            ),
          },
        }))}
      />
    </>
  );

  const tipos = (selecionados: string[]) => (
    <Grid doubling columns={3}>
      {opcoesPorDimensao.types.map((tipo) => (
        <Grid.Column key={tipo.id}>
          <Checkbox
            label={traduzir(tipo.title)}
            checked={selecionados.includes(tipo.id)}
            onChange={() => alternarRestricao('types', selecionados, tipo.id)}
          />
        </Grid.Column>
      ))}
    </Grid>
  );

  const rotulos = {
    toolbar: intl.formatMessage(messages.secaoToolbar),
    types: intl.formatMessage(messages.secaoTypes),
    layouts: intl.formatMessage(messages.secaoLayouts),
    blocks: intl.formatMessage(messages.secaoBlocks),
    fields: intl.formatMessage(messages.secaoFields),
  };

  // Categorias dentro de "Permissões e restrições": primeiro o que libera,
  // depois o que restringe.
  const categorias = [
    {
      key: 'paineis',
      title: tituloComContador(
        intl.formatMessage(messages.secaoPaineis),
        controlpanels.length,
      ),
      content: { content: paineis },
    },
    {
      key: 'extras',
      title: tituloComContador(
        intl.formatMessage(messages.secaoExtras),
        extras.length,
      ),
      content: { content: permissoesAdicionais },
    },
    {
      key: 'toolbar',
      title: tituloComContador(
        rotulos.toolbar,
        restricoes.toolbar?.length ?? null,
      ),
      content: {
        content: secaoRestritiva(
          'toolbar',
          rotulos.toolbar,
          intl.formatMessage(messages.ajudaToolbar),
          listaSimples('toolbar'),
        ),
      },
    },
    {
      key: 'types',
      title: tituloComContador(rotulos.types, restricoes.types?.length ?? null),
      content: {
        content: secaoRestritiva(
          'types',
          rotulos.types,
          intl.formatMessage(messages.ajudaTypes),
          tipos,
        ),
      },
    },
    {
      key: 'layouts',
      title: tituloComContador(
        rotulos.layouts,
        restricoes.layouts?.length ?? null,
      ),
      content: {
        content: secaoRestritiva(
          'layouts',
          rotulos.layouts,
          intl.formatMessage(messages.ajudaLayouts),
          listaSimples('layouts'),
        ),
      },
    },
    {
      key: 'blocks',
      title: tituloComContador(
        rotulos.blocks,
        restricoes.blocks?.length ?? null,
      ),
      content: {
        content: secaoRestritiva(
          'blocks',
          rotulos.blocks,
          intl.formatMessage(messages.ajudaBlocks),
          acordeoesDeBlocos,
        ),
      },
    },
    {
      key: 'fields',
      title: tituloComContador(
        rotulos.fields,
        restricoes.fields?.length ?? null,
      ),
      content: {
        content: secaoRestritiva(
          'fields',
          rotulos.fields,
          intl.formatMessage(messages.ajudaFields),
          listaSimples('fields'),
        ),
      },
    },
  ];

  const gruposEUsuarios = (
    <>
      <Ajuda>{intl.formatMessage(messages.ajudaPrincipals)}</Ajuda>
      <List divided relaxed>
        {principais.map((principal) => (
          <List.Item key={principal.id}>
            <List.Content floated="right">
              <Button
                type="button"
                size="mini"
                negative
                basic
                onClick={() => setRemovendo(principal)}
              >
                {intl.formatMessage(messages.removePrincipal)}
              </Button>
            </List.Content>
            <List.Content>
              {principal.title}{' '}
              <small>
                ({tipoDoPrincipal(principal)}: {principal.id})
              </small>
            </List.Content>
          </List.Item>
        ))}
      </List>
      <PrincipaisPicker
        ignorar={principais.map((p) => p.id)}
        onSelecionar={(principal) => setPrincipais([...principais, principal])}
      />
    </>
  );

  const secoes = [
    {
      key: 'identificacao',
      title: intl.formatMessage(messages.secaoGeral),
      content: { content: identificacao },
    },
    {
      key: 'permissoes',
      title: intl.formatMessage(messages.secaoPermissoes),
      content: {
        content: (
          <>
            <Ajuda>{intl.formatMessage(messages.ajudaPermissoes)}</Ajuda>
            <Accordion.Accordion
              exclusive={false}
              className="mpf-perfil-categorias"
              panels={categorias}
            />
          </>
        ),
      },
    },
    {
      key: 'principals',
      title: tituloComContador(
        intl.formatMessage(messages.secaoPrincipals),
        principais.length,
      ),
      content: { content: gruposEUsuarios },
    },
  ];

  return (
    // noValidate: com seções recolhidas o navegador bloquearia o envio em
    // silêncio num campo obrigatório escondido; o backend devolve a mensagem.
    <Form noValidate onSubmit={salvar} loading={salvando} error={Boolean(erro)}>
      <Message error content={erro} />

      <Accordion
        fluid
        styled
        exclusive={false}
        defaultActiveIndex={[0]}
        className="mpf-perfil-secoes"
        panels={secoes}
      />

      <Segment basic>
        <Button primary type="submit" disabled={salvando}>
          <FormattedMessage id="Save" defaultMessage="Salvar" />
        </Button>
        <Button type="button" onClick={onCancelar} disabled={salvando}>
          <FormattedMessage id="Cancel" defaultMessage="Cancelar" />
        </Button>
      </Segment>

      <Confirm
        open={Boolean(removendo)}
        header={intl.formatMessage(messages.removePrincipal)}
        content={mensagemDeRemocao(removendo)}
        confirmButton={
          <Button negative>
            {intl.formatMessage(messages.removePrincipal)}
          </Button>
        }
        cancelButton={intl.formatMessage(messages.cancel)}
        onCancel={() => setRemovendo(null)}
        onConfirm={() => {
          if (removendo) {
            setPrincipais(principais.filter((p) => p.id !== removendo.id));
          }
          setRemovendo(null);
        }}
      />
    </Form>
  );
};

export default PerfilEditor;
